<template lang="pug">
.service.row-container
  .aside.col-container.col-4(:style="asideStyle")
    div.aside__top
      h1.aside__head УСЛУГИ
      p.aside__subtitle(v-html="serviceCommonSubtitle")
      ul.aside__menu
        li.aside__menu-item(
          v-for="item in serviceMenuItems")
          NuxtLink.aside__menu-link(
            :to="`/uslugi/${item.url}/`"
            :class="[item.url === $route.params.slug ? 'aside__menu-link--active' : '']")
            span(v-html="item.menuTitle")
    div.aside__bottom(v-if="serviceData.serviceBlocks.length")
      div
        div.aside__bottom-item.aside__random
          span.aside__random-title {{ randomFact.title}}
          span.aside__random-text(v-html="randomFact.text[0]")
          span.aside__random-text(v-html="randomFact.text[1]")
        div.aside__bottom-item.aside__tech(v-if="title && sideInfoData && sideInfoData.info.length")
          h4.aside__tech-title {{ sideInfoData.title }}
          ul.aside__tech-list
            li.aside__tech-list-item(v-for="item in sideInfoData.info") {{ item }}
      div.aside__bottom-item.aside__review
  div.content.col-container.col-7(
    v-if="serviceData.serviceBlocks.length")
    img.service__image(
      :src="imgSrc"
      :alt="$route.params.slug"
    )
    h2.service__title.service__title--purple(v-html="serviceData.title")
    p.service__text(v-html="serviceData.text")
    div.service__block(v-for="block in serviceData.serviceBlocks")
      h3.service__title(v-if="block.title" v-html="block.title")
      TaskComponent(
        v-if="block.key === 'task'"
        :block="getBlockByKey(block.key)")
      ProcessComponent(
        v-if="block.key === 'process'"
        :block="getBlockByKey(block.key)")
      FeatureComponent(
        v-if="block.key === 'feature'"
        :block="getBlockByKey(block.key)")
      PaymentComponent(
        v-if="block.key === 'payment'"
        :block="getBlockByKey(block.key)")
      PortfolioComponent(
        v-if="block.key === 'portfolio'"
        :block="getBlockByKey(block.key)")
      FaqComponent(
        v-if="block.key === 'faq'"
        :block="getBlockByKey(block.key)")
      PriceComponent(
        v-if="block.key === 'price'"
        :block="getBlockByKey(block.key)")
      CustomBlockComponent(
        v-if="block.key === 'customBlock'"
        :block="getBlockByKey(block.key)")
  div.content.content__parser.col-container.col-7(v-else)
    h1.service__title {{ title }}
    ContentParser(:content="content")
  div.service__footer.col-container
    ServicesFooter(
      :formValue="footer_data"
      :footerTitle="footerTitle")
</template>

<script setup>
import { storeToRefs } from "pinia"
import { serviceMenuItems, sideInfo, serviceCommonSubtitle, footerTitle } from "~/data/services/serviceData"
import { useServicesStore } from '~/stores/services'

const randomFact = reactive({
  title: 'Рандомный NeФакт №238:', text: ['Для ведения задач мы&nbsp;используем Трелло.', 'A&nbsp;чтобы тщательно смотреть за&nbsp;выполнением задач, мы&nbsp;написали для Трелло свой плагин.']
})

const route = useRoute()
const page_slug = route.params.slug

const servicesStore = useServicesStore()
await servicesStore.setSlugPage(page_slug)
const { title, subtitle, footer_data, content, meta, og } = storeToRefs(servicesStore)

const sideInfoData = computed(() => {
  return sideInfo.find(
    info => info.url === page_slug
  )
})

const serviceData = computed(() => {
  return serviceMenuItems.find(
    item => item.url === page_slug
  )
})

const asideStyle = computed(() => {
  return {
    '--height': serviceData.value?.serviceBlocks.length ? '93rem' : 'auto',
  }
})

const imgSrc = computed(() => {
  return serviceData.value.imgSrc
    ? `/services/${serviceData.value.imgSrc}.svg`
    : "/services/placeholder.svg"
})

function getBlockByKey(key) {
  return serviceData.value.serviceBlocks.find(
    block => block.key === key
  )
}

function setSmoothScroll(e) {
  e.preventDefault()
  const target = e.target

  document.querySelector(target.getAttribute('href')).scrollIntoView({
    behavior: 'smooth'
  })
}

usePageHeadMeta(meta.value.title, meta.value.description, og.value)

onMounted(() => {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', setSmoothScroll)
  })
})
</script>

<style lang="scss" scoped>
.service {
  --title-height: 34px;

  display: flex;
  justify-content: space-between;
  color: $dark;

  &__image {
    display: block;
    width: 100%;
    aspect-ratio: 2.05;
    object-fit: cover;
    margin: var(--title-height) 0 3.1rem;
  }

  &__title {
    text-transform: uppercase;
    color: $primary;
    font-size: 1.25rem;
    margin-bottom: 3rem;

    &--purple {
      color: $purple;
      margin-bottom: 2rem;
    }
  }

  &__block {
    margin-top: 100px;
  }
}

.aside {
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 1023px) {
    display: none;
  }

  &__head {
    color: $primary;
    margin-bottom: 2rem;
    font-size: var(--title-height);
  }

  &__subtitle {
    color: $primary;
    margin-bottom: 70px;
    line-height: 24px;
  }

  &__top {
    height: var(--height);
  }

  &__menu {
    font-size: 16px;
    font-weight: bold;
    max-width: 16ch;
  }

  &__menu>*+* {
    margin-top: 0.88rem;
  }

  &__menu-item {
    line-height: 24px;
  }

  &__menu-link {
    color: $gray;

    &:hover {
      color: $purple;
    }

    &--active {
      color: $primary;
    }
  }

  &__bottom {
    flex-grow: 2;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  &__tech {
    margin-top: 22.4rem;
  }

  &__random-title,
  &__random-text {
    display: block;
    font-size: 13px;
    color: $gray;
    width: 28ch;
  }

  &__random-title {
    margin-bottom: 0.5rem;
  }

  &__tech {
    border-top: 2px solid $gray;
    border-bottom: 2px solid $gray;
    padding: 2.6rem 0;
    font-size: 16px;
    line-height: 1.4;
    color: $gray;
    width: 23ch;
  }

  &__tech-title {
    color: $primary;
    font-size: 16px;
    font-weight: 400;
    margin: 0;
  }

  &__tech-list-item {
    margin-top: 1rem;
  }
}

a.form-anchor.form-anchor {
  text-decoration: underline;
}
</style>

<style lang="scss">
.content__parser {
  h3 {
    margin-bottom: 0.5rem;
  }

  ul {
    margin-bottom: 1rem;
  }
}
</style>
